// TGCLOUD-1818 — one criterion, rendered.
//
// NOT an MUI <Chip>. Chip's delete affordance is an SVG with a click handler,
// not a focusable control, and its accessible name cannot be set to anything
// useful. A chip here has TWO actions on one shape — edit (the body) and
// remove (the cross) — and both have to be real buttons or a keyboard user
// cannot reach the second one. That is worth a small custom component;
// fighting Chip to produce a nested button would cost more.
//
// Dumb by construction: it emits onEdit/onRemove and owns nothing.

import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { styled, alpha } from '@mui/material/styles';

const Root = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  maxWidth: '100%',
  borderRadius: 999,
  // Tinted from the tenant's own primary, so a customer who has themed the app
  // gets chips in their colour rather than a second hardcoded blue. Kept light
  // so text.secondary still clears 4.5:1 against it.
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
  height: 28
}));

const Body = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  maxWidth: '100%',
  padding: theme.spacing(0, 0.5, 0, 1.25),
  border: 0,
  background: 'none',
  font: 'inherit',
  fontSize: '0.8125rem',
  color: theme.palette.text.primary,
  cursor: 'pointer',
  borderRadius: '999px 0 0 999px',
  height: '100%',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2
  }
}));

const FieldName = styled('span')({
  whiteSpace: 'nowrap'
});

const Operator = styled('span')(({ theme }) => ({
  // Muted and smaller, but still a real text colour — text.secondary is
  // ~4.6:1 on this tint in light mode and ~9:1 in dark. Do not drop it to
  // text.disabled to make it "quieter"; that fails contrast.
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  whiteSpace: 'nowrap'
}));

const Value = styled('span')({
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

const Remove = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  marginRight: 2,
  padding: 0,
  border: 0,
  background: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  // Visible at rest. A remove affordance that only appears on hover is
  // unreachable by touch and invisible to anyone scanning the bar.
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.16),
    color: theme.palette.text.primary
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2
  }
}));

/**
 * @param {object} props
 * @param {string} props.fieldLabel   e.g. "Family ID"
 * @param {string} props.operatorLabel e.g. "is"
 * @param {string} props.valueLabel    truncated value, e.g. "G-0-01520"
 * @param {string} props.fullSentence  untruncated "Family ID is G-0-01520"
 * @param {() => void} props.onEdit
 * @param {() => void} props.onRemove
 * @param {string} props.removeLabel   fully-qualified accessible name
 * @param {string} props.editLabel     fully-qualified accessible name
 */
export default function FilterChip({
  fieldLabel,
  operatorLabel,
  valueLabel,
  fullSentence,
  onEdit,
  onRemove,
  removeLabel,
  editLabel
}) {
  return (
    <Root>
      <Body
        type="button"
        onClick={onEdit}
        title={fullSentence}
        aria-label={editLabel}
      >
        <FieldName>{fieldLabel}</FieldName>
        <Operator>{operatorLabel}</Operator>
        {valueLabel ? <Value>{valueLabel}</Value> : null}
      </Body>
      <Remove type="button" onClick={onRemove} aria-label={removeLabel}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </Remove>
    </Root>
  );
}
